import { FC } from "react";
import * as _ from "lodash";
import moment from "moment";
import { useSelector } from "react-redux";

import { classname, Epoch, TS, Units, UnitSystem } from "@ctra/utils";
import { useTranslation, Enterprise } from "@ctra/i18n";
import { Col, LoadingOutlined, GamePlan, Grid, Row, Typography } from "@ctra/components";

import {
  DataDescriptorEntity,
  EnterpriseAppState,
  GamePlanEntity,
  Enterprise as API,
  TodoItemStatus
} from "@ctra/api";

import { GamePlanLayout, TodoListProvider } from "@game-plans";
import { useFarmList } from "@farms";
import { useScorecard } from "@scorecard";
import { useLocalization } from "@base";
import { useUnit } from "@chart";

import styles from "./GamePlanItem.module.less";

const { Text } = Typography;
const {
  V2: {
    List: { ItemWrapper, Title, Period, TaskOverview, NumericValue, KPI, KPIOverview }
  }
} = GamePlan;

type GamePlanItemProps = GamePlanEntity & {
  /**
   * Click to open game plan details
   */
  onClick: () => void;
};

/**
 * Game plan item
 * @param onClick
 * @param gamePlan
 * @constructor
 */
export const GamePlanItem: FC<GamePlanItemProps> = ({ onClick, ...gamePlan }) => {
  const { t } = useTranslation();
  const { unitSystem: globalUnitSystem } = useLocalization();
  const { md } = Grid.useBreakpoint();
  const { farmList, isLoading: isFarmListLoading } = useFarmList();

  const {
    meta: { isLoading },
    value: scoreCardValue
  } = useScorecard();

  const {
    id,
    title,
    dataDescriptorID,
    farmID,
    goal,
    timePeriod: { startEpoch, endEpoch },
    unitSystem
  } = gamePlan;

  const {
    unitSystem: { unit: tUnit },
    kpi,
    gamePlan: {
      labels,
      list: { headers },
      todo: { status }
    }
  } = Enterprise;

  const { value } = goal;

  /**
   * Get the column widths for the given setting
   */
  const { title: titleSpan, tasks, period: periodSpan, current, kpi: kpiSpan, target } = GamePlanLayout;

  const {
    dataProperties: { typeName },
    valueProperties: { units, acquisitionMethod }
  } = useSelector<EnterpriseAppState, DataDescriptorEntity>((state) =>
    API.entities.getDataDescriptor(state, { id: dataDescriptorID })
  );

  const currentUnit = useUnit(dataDescriptorID);

  /**
   * Checks if there's a mismatch between the unit systems of created game plans
   * and our current global unit system
   * convert to our system if mismatch found
   */
  const getGoalValue = (): number =>
    Units.convertToUnitSystem(value, units, {
      from: unitSystem as UnitSystem,
      to: globalUnitSystem
    });

  const TitleColumn = isFarmListLoading ? (
    <LoadingOutlined />
  ) : (
    farmList && (
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      <Title farm={farmList[farmID].name!} startDate={startEpoch}>
        {title}
      </Title>
    )
  );

  return (
    <TodoListProvider gamePlanID={id}>
      {(todoList) => {
        const todoListValues = _.values(todoList);

        const overdueCount = _.filter(
          todoListValues,
          (item) => item.dueDateTs && TS.asMoment(item.dueDateTs).isBefore(moment())
        ).length;

        const doneCount = _.filter(todoListValues, (item) => item.status === TodoItemStatus.Completed).length;
        const todoCount = todoListValues.length - doneCount;

        const mobileView = (
          <section className={classname(styles.Mobile, overdueCount ? styles.Overdue : "")} onClick={onClick}>
            <Row gutter={[0, 8]} className={styles.Section}>
              <Col span={24} className={styles.Title}>
                {TitleColumn}
              </Col>
              <Col span={24}>
                <Period
                  startEpoch={startEpoch}
                  endEpoch={endEpoch}
                  untill={t<string>(labels.deadline, {
                    timeUntil: Epoch.until(endEpoch)
                  })}
                />
              </Col>
            </Row>
            <Row className={styles.Section}>
              <Col>
                <KPIOverview
                  headers={{
                    title: t(headers.title),
                    current: t(headers.current),
                    target: t(headers.target)
                  }}
                  KPIType={t<string>(kpi.displayName(typeName), {
                    case: null,
                    variant: null,
                    makeDefaultValue: true
                  })}
                  acquisitionMethod={t<string>(kpi.acquisitionMethod(acquisitionMethod))}
                  current={{ isLoading, scoreCardValue, unit: currentUnit }}
                  targetValue={getGoalValue()}
                />
              </Col>
            </Row>
            <Row gutter={[0, 8]} className={styles.Wrapper}>
              <Col>
                <Text type="secondary">{t(labels.todo)}</Text>
              </Col>
              <Col span={24}>
                <TaskOverview
                  headers={{
                    todo: t(status.todo),
                    done: t(status.done),
                    overdue: t(status.overdue, { overdueTasksCount: null })
                  }}
                  todo={todoCount}
                  done={doneCount}
                  overdue={overdueCount}
                />
              </Col>
            </Row>
          </section>
        );

        return !md ? (
          mobileView
        ) : (
          <ItemWrapper isOverdue={!!overdueCount} onClick={onClick}>
            <Col span={titleSpan} className={styles.Title}>
              {TitleColumn}
            </Col>
            <Col span={kpiSpan}>
              <KPI
                name={t<string>(kpi.displayName(typeName), {
                  case: null,
                  variant: null,
                  makeDefaultValue: true
                })}
                acquisitionMethod={t<string>(kpi.acquisitionMethod(acquisitionMethod))}
              />
            </Col>
            <Col span={current}>
              <NumericValue
                highlighted
                isLoading={isLoading}
                value={scoreCardValue}
                unit={t<string>(tUnit(currentUnit))}
              />
            </Col>
            <Col span={target}>
              <NumericValue value={getGoalValue()} unit={t<string>(tUnit(currentUnit))} />
            </Col>
            <Col span={tasks}>
              <TaskOverview
                headers={{
                  todo: t(status.todo),
                  done: t(status.done),
                  overdue: t(status.overdue, { overdueTasksCount: null })
                }}
                todo={todoCount}
                done={doneCount}
                overdue={overdueCount}
              />
            </Col>
            <Col span={periodSpan}>
              <Period
                startEpoch={startEpoch}
                endEpoch={endEpoch}
                untill={t<string>(labels.deadline, {
                  timeUntil: Epoch.until(endEpoch)
                })}
              />
            </Col>
          </ItemWrapper>
        );
      }}
    </TodoListProvider>
  );
};
